// Metafield keys are displayed as "namespace.name" when a namespace exists.
// A namespace-less name may itself begin with a dot (for example ".CC-FBA Order");
// that leading dot is part of the name, not an empty namespace separator.
export const parseMetafieldKey = (key) => {
  if (key && typeof key === 'object') {
    const namespace =
      typeof key.namespace === 'string' && key.namespace.trim() ? key.namespace.trim() : null;
    const name = typeof key.name === 'string' ? key.name.trim() : '';
    if (name) return { namespace, name };
    if (typeof key.key === 'string') return parseMetafieldKey(key.key);
  }
  const raw = typeof key === 'string' ? key.trim() : String(key ?? '').trim();
  if (!raw) return { namespace: null, name: '' };

  const separator = raw.indexOf('.');
  const name = separator > 0 ? raw.slice(separator + 1) : raw;
  // Human-readable keys with spaces and leading-dot keys are namespace-less names.
  // A namespace is only meaningful when both segments are non-empty and the key
  // follows the compact namespace.name form.
  if (separator > 0 && name && !raw.includes(' ')) {
    return { namespace: raw.slice(0, separator), name };
  }
  return { namespace: null, name: raw };
};

export const getMetafieldDisplayKey = (metafield) => {
  if (typeof metafield === 'string') return metafield.trim();
  if (!metafield || typeof metafield !== 'object') return '';

  const namespace =
    typeof metafield.namespace === 'string' ? metafield.namespace.trim() : metafield.namespace;
  const name = typeof metafield.name === 'string' ? metafield.name.trim() : metafield.name;
  if (name) return namespace ? `${namespace}.${name}` : name;

  // Legacy/product and manually-entered records may only have a flat key.
  return typeof metafield.key === 'string' ? metafield.key.trim() : '';
};

// This token is for form option identity only. It is deterministic from the
// visible mapping fields, not an internal database ID or discovery index.
export const getMetafieldOptionValue = (metafield) => {
  const parsed = parseMetafieldKey(metafield);
  return JSON.stringify([parsed.namespace, parsed.name]);
};

export const getMetafieldRowKey = (metafield, value = null) => {
  const parsed = parseMetafieldKey(metafield);
  return JSON.stringify([parsed.namespace, parsed.name, value]);
};

export const buildMetafieldMatch = (key, value) => {
  const parsed = parseMetafieldKey(key);
  return {
    namespace: parsed.namespace,
    name: parsed.name,
    ...(value ? { value } : {}),
  };
};

export const buildPoMetafieldMatch = (key, value) => {
  const parsed = buildMetafieldMatch(key, value);
  return {
    namespace: parsed.namespace,
    key: parsed.name,
    ...(parsed.value ? { value: parsed.value } : {}),
  };
};

export const buildOrderMetafieldTestPayload = (storeId, key, value, sampleValues = []) => {
  const rule = buildMetafieldMatch(key, value);
  const sampleValue = value || sampleValues.find((candidate) => candidate != null) || '';
  return {
    storeId,
    mappingRules: { order_metafield_match: rule },
    samplePayload: {
      meta: [{ namespace: rule.namespace, name: rule.name, value: sampleValue }],
    },
  };
};

export const normalizeTestMappingResult = (response) => {
  const count = response?.matchedCount ?? response?.count;
  return {
    count: count != null ? Number(count) : null,
    matched: typeof response?.matched === 'boolean' ? response.matched : null,
    clientId: response?.clientId ?? response?.client?.id ?? null,
    samples: response?.sample ?? response?.samples ?? response?.products ?? [],
  };
};
